import { useEffect, useState, type FormEvent } from "react";
import { LoaderCircle, X } from "lucide-react";

interface CreateProjectDialogProps {
  open: boolean;
  busy: boolean;
  error: string;
  workspaceRoot: string;
  onClose(): void;
  onCreate(input: {
    name: string;
    description: string;
    objective: string;
    successCriteria: string;
    nextAction: string;
    trackingMode: "auto";
    workspacePath: string;
  }): Promise<void>;
}

export function CreateProjectDialog(props: CreateProjectDialogProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [objective, setObjective] = useState("");
  const [workspacePath, setWorkspacePath] = useState(`${props.workspaceRoot}/nouveau-projet`);
  const [pathEdited, setPathEdited] = useState(false);

  useEffect(() => {
    if (!props.open) return;
    setName("");
    setDescription("");
    setObjective("");
    setWorkspacePath(`${props.workspaceRoot}/nouveau-projet`);
    setPathEdited(false);
  }, [props.open, props.workspaceRoot]);

  function updateName(value: string) {
    setName(value);
    if (!pathEdited) setWorkspacePath(`${props.workspaceRoot}/${slugify(value) || "nouveau-projet"}`);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (props.busy) return;
    await props.onCreate({
      name,
      description,
      objective,
      successCriteria: "",
      nextAction: "",
      trackingMode: "auto",
      workspacePath,
    });
  }

  if (!props.open) return null;
  return (
    <div className="dialog-layer" role="presentation">
      <button className="dialog-backdrop" type="button" onClick={props.onClose} disabled={props.busy} aria-label="Fermer" />
      <section className="create-dialog" role="dialog" aria-modal="true" aria-busy={props.busy} aria-labelledby="create-project-title">
        <header>
          <h2 id="create-project-title">Nouveau projet</h2>
          <button className="icon-command" type="button" onClick={props.onClose} disabled={props.busy} aria-label="Fermer"><X aria-hidden="true" /></button>
        </header>
        <form onSubmit={submit}>
          <label className="form-field">
            <span>Nom</span>
            <input aria-label="Nom" value={name} onChange={(event) => updateName(event.target.value)} maxLength={120} autoFocus />
          </label>
          <label className="form-field">
            <span>Dossier</span>
            <input aria-label="Dossier" value={workspacePath} onChange={(event) => { setWorkspacePath(event.target.value); setPathEdited(true); }} maxLength={2000} />
          </label>
          <label className="form-field">
            <span>Finalité</span>
            <textarea aria-label="Finalité" value={objective} onChange={(event) => setObjective(event.target.value)} maxLength={2000} rows={3} />
          </label>
          <label className="form-field">
            <span>Description</span>
            <textarea aria-label="Description" value={description} onChange={(event) => setDescription(event.target.value)} maxLength={2000} rows={3} />
          </label>
          {props.error ? <p className="form-error" role="alert">{props.error}</p> : null}
          <footer>
            <button className="text-command" type="button" onClick={props.onClose} disabled={props.busy}>Annuler</button>
            <button className="text-command primary" type="submit" disabled={props.busy || !name.trim() || !objective.trim() || !workspacePath.trim()}>{props.busy ? <LoaderCircle className="spin" aria-hidden="true" /> : null}{props.busy ? "Création…" : "Créer"}</button>
          </footer>
        </form>
      </section>
    </div>
  );
}

function slugify(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}
